import { useState } from "react";
import { ArrowRight, CheckCircle2, GitBranch, GitPullRequest, GitCommit, Shield } from "lucide-react";
import bitbucketLogo from "@/assets/bitbucket-logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { requestAccessCsv } from "@/lib/utils";
import { validateEmail } from "@/lib/emailValidation";
import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Link } from "react-router-dom";

export default function Bitbucket() {
  // Keep this page render-safe; test-only crash code here breaks the production landing page.

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateEmail(email);
    if (!validation.valid) {
      toast({ title: "Invalid email", description: validation.message, variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await requestAccessCsv(email);
      if (!response.ok) throw new Error('Request failed');
      setIsSubmitted(true);
      toast({ title: "Success!", description: "You've been added to the waitlist" });
    } catch (error) {
      // Non-PII log for production debugging (avoid logging the user's email).
      console.error("[Bitbucket] Failed to join waitlist", error);
      toast({ title: "Error", description: "Failed to join waitlist. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ThemeToggle />
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">← Back to Home</Link>
        </div>
      </section>

      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                <GitBranch className="w-4 h-4" />
                <span className="text-sm font-medium">Bitbucket Integration</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Automated Pull Requests on Bitbucket
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Healion integrates with Bitbucket to automatically detect issues and generate pull requests with AI-powered fixes for your repositories.
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl bg-background border border-border flex items-center justify-center p-12">
              <img src={bitbucketLogo} alt="Bitbucket Logo" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Bitbucket Integration Works</h2>
            <p className="text-xl text-muted-foreground">Automated bug fixes from detection to pull request</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Connect Repository", description: "Link your Bitbucket repos and grant Healion access", icon: GitBranch },
              { step: "2", title: "Analyze Codebase", description: "AI scans your code for bugs and security issues", icon: Shield },
              { step: "3", title: "Generate Fixes", description: "Production-ready fixes following your conventions", icon: GitCommit },
              { step: "4", title: "Create PR", description: "Automatic pull request with tests and descriptions", icon: GitPullRequest },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <div className="text-sm text-primary font-semibold mb-2">Step {item.step}</div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Healion for Bitbucket?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Bitbucket Pipelines", description: "Works seamlessly with Bitbucket Pipelines for automated testing and deployment" },
              { title: "Jira Integration", description: "Automatically links fixes to Jira issues for complete traceability" },
              { title: "Team Collaboration", description: "PRs follow your team's review process and coding standards" },
            ].map((benefit, index) => (
              <div key={index} className="bg-background rounded-xl p-6 border border-border">
                <CheckCircle2 className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Transform Your Bitbucket Workflow</h2>
          <p className="text-xl text-muted-foreground mb-8">Join the waitlist for Healion's Bitbucket integration</p>
          {isSubmitted ? (
            <div className="flex items-center justify-center gap-2 text-primary">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">You're on the waitlist! We'll be in touch soon.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1" disabled={isSubmitting} />
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? "Joining..." : "Join Waitlist"}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
